import {Form} from "@/utils/Form";
import {DomainAllocationsData} from "./dynamicDataSource";
import {useDomainAllocationUpdateMutation} from "../mutations.generated";
import {DomainAllocationUpdateMutation, DomainAllocationUpdateMutationVariables} from "../mutations.generated";
import {ErrorWithContext} from "@/utils/ErrorHelper";
import {AllocationType} from "./types";
import {ServicesDataSourceData} from "./servicesDataSource";
import {useState} from "react";
import {DeepPartial} from "@apollo/client/utilities";

/** Services domain allocation is a bit different on the API, but we want to keep the same form structure
 * for consistency.
 * */
export class DynamicDomainAllocationForm extends Form<
    DomainAllocationUpdateMutation,
    DomainAllocationUpdateMutationVariables,
    DomainAllocationsData
> {
    readonly domainId: number;
    readonly allocationType: AllocationType;

    constructor(domainId: number, allocationType: AllocationType, initialData: DomainAllocationsData) {
        super("domainAllocationUpdate");
        this.hooks(useDomainAllocationUpdateMutation, initialData);
        this.domainId = domainId;
        this.allocationType = allocationType;
    }

    /**
     * Returns the backend errors for the proper fields.
     */
    get errorsWithContext(): Record<string, ErrorWithContext[]> {
        const mappedErrors: Record<string, ErrorWithContext[]> = {};

        for (const error of structuredClone(this.errors)) {
            const errorContext = JSON.parse(JSON.parse(error.context)["context"]);
            if (mappedErrors[errorContext["name"]] == undefined) {
                mappedErrors[errorContext["name"]] = [];
            }
            mappedErrors[errorContext["name"]].push(error as unknown as ErrorWithContext);
        }

        return mappedErrors;
    }

    updateAllocation(name: string, allocation: number) {
        const allocations = structuredClone(this.values.allocations);
        const target = allocations.find((p) => p.name === name);
        target.newAllocation = allocation;
        this.updateValues({allocations: allocations});
    }

    override get filteredVariables(): DomainAllocationUpdateMutationVariables {
        const values = structuredClone(this.values);

        return {
            domainId: this.domainId,
            [this.allocationType]: values.allocations
                .filter((allocation) => allocation.newAllocation !== allocation.domainAllocated)
                .map((allocation) => ({
                    name: allocation.name,
                    value: allocation.newAllocation,
                })),
        };
    }
}

// NOTE: Even though only ccAattendant has 'included' field, we are
// making 'included' generic for all fields here to make it easier to work with AllocationTable
// component, as it is completely generic.
export type ServicesDomainAllocationFormData = {
    [K in keyof ServicesDataSourceData]: ServicesDataSourceData[K] & {
        newDomainAllocated: ServicesDataSourceData[K]["domainAllocated"];
    } & (ServicesDataSourceData[K]["domainAllocated"] extends number ? {included?: number} : unknown);
};

type CCToggleCache = {
    ccAattendant: number | undefined;
    ccQueue: number | undefined;
};

export class ServicesDomainAllocationForm extends Form<
    DomainAllocationUpdateMutation,
    DomainAllocationUpdateMutationVariables,
    ServicesDomainAllocationFormData
> {
    readonly domainId: number;

    /* Saving the CC related allocations before disabling them, so that it feels nicer reenabling them  */
    private ccToggleCache: CCToggleCache;
    private setCcToggleCache: React.Dispatch<React.SetStateAction<CCToggleCache>>;

    constructor(domainId: number, dataSourceData: ServicesDataSourceData) {
        super("domainAllocationUpdate");
        this.hooks(useDomainAllocationUpdateMutation, ServicesDomainAllocationForm.createInitialData(dataSourceData));
        this.domainId = domainId;

        [this.ccToggleCache, this.setCcToggleCache] = useState({
            ccAattendant: undefined,
            ccQueue: undefined,
        });
    }

    static createInitialData(data: ServicesDataSourceData): ServicesDomainAllocationFormData {
        const result = structuredClone(data) as ServicesDomainAllocationFormData;

        for (const key of Object.keys(result) as (keyof ServicesDataSourceData)[]) {
            if ((key as unknown) === "__typename") continue;

            const item = result[key];
            item.newDomainAllocated = item.domainAllocated ?? 0;
        }

        result.ccAattendant.included = 1;

        return result;
    }

    /**
     * Returns the backend errors for the proper fields.
     */
    get errorsWithContext(): Record<string, ErrorWithContext[]> {
        const mappedErrors: Record<string, ErrorWithContext[]> = {};

        for (const error of structuredClone(this.errors)) {
            // location is like ["services.ccBase.value"]
            const name = error.location[0].split(".")[1];

            if (mappedErrors[name] == undefined) {
                mappedErrors[name] = [];
            }
            mappedErrors[name].push(error as unknown as ErrorWithContext);
        }

        return mappedErrors;
    }

    updateAllocation<K extends keyof ServicesDomainAllocationFormData, V extends K extends "ccBase" ? boolean : number>(
        name: K,
        allocation: V,
    ) {
        const update: DeepPartial<ServicesDomainAllocationFormData> = {
            [name]: {
                newDomainAllocated: allocation,
            },
        };

        if (name === "ccBase" && allocation !== this.values.ccBase.newDomainAllocated) {
            if (allocation) {
                update.ccAattendant = {newDomainAllocated: this.ccToggleCache.ccAattendant ?? 0};
                update.ccQueue = {newDomainAllocated: this.ccToggleCache.ccQueue ?? 0};
            } else {
                this.setCcToggleCache({
                    ccAattendant: this.values.ccAattendant.newDomainAllocated,
                    ccQueue: this.values.ccQueue.newDomainAllocated,
                });

                update.ccAattendant = {newDomainAllocated: 0};
                update.ccQueue = {newDomainAllocated: 0};
            }
        }

        this.updateValues(update);
    }

    override get filteredVariables(): DomainAllocationUpdateMutationVariables {
        const values = structuredClone(this.values);

        return {
            domainId: this.domainId,
            services: {
                fax: values.fax.newDomainAllocated,
                group5: values.group5.newDomainAllocated,
                group10: values.group10.newDomainAllocated,
                group20: values.group20.newDomainAllocated,
                ccBase: values.ccBase.newDomainAllocated,
                ccAattendant: values.ccAattendant.newDomainAllocated,
                ccQueue: values.ccQueue.newDomainAllocated,
            },
        };
    }
}
